import { LOGOUT_RTK } from "@/components/access/ducks/Actions"
import { createSlice } from "@reduxjs/toolkit"
import {
  deleteJobPostThunk,
  editJobPostThunk,
  fetchAllJobPostsThunk,
  fetchJobPostThunk,
  submitJobPostThunk,
} from "./thunks"

/**
 * @template T
 * @typedef {import("@reduxjs/toolkit").PayloadAction<T>} TAction<T>
 */

/** @typedef {import("./types").JobPostResetState} JobPostResetState */
/** @typedef {import("./types").JobPostState} JobPostState */
/** @typedef {import("./types").JobPost} JobPost */

/**
 * Reset state for the job posts slice
 * @type {JobPostResetState}
 */
const resetState = {
  jobTitle: "",
  jobCompany: "",
  jobDescription: "",
  jobApplicationLink: "",
  jobExpirationDate: "",
  jobLocationDict: { city: "", stateName: "", stateCode: "", countryCode: "" },
  jobId: null,
  jobIsRemote: false,

  loading: false,

  errorMessage: "",
  errorCount: 0,
}

/** @type {JobPostState} */
const initialState = {
  ...resetState,
  jobPosts: [],
  selectedJobPost: null,
  postedByConstituent: null,
  suggestedSearch: null,
}

/**
 * @param {JobPostState} state
 * @param {import("./types").FetchJobPostsResult} data
 */
const receiveJobPostAndConstituent = (state, data) => {
  state.selectedJobPost = data.selectedJobPost
  state.postedByConstituent = data.postedByConstituent
  state.suggestedSearch = data.suggestedSearch
}

const jobPostsSlice = createSlice({
  name: "jobPosts/user",
  initialState,
  reducers: {
    /** @param {TAction<string>} action */
    updateJobTitle: (state, action) => {
      state.jobTitle = action.payload
    },
    /** @param {TAction<string>} action */
    updateJobCompany: (state, action) => {
      state.jobCompany = action.payload
    },
    toggleJobIsRemote: (state) => {
      state.jobIsRemote = !state.jobIsRemote
    },
    /** @param {TAction<string>} action */
    updateJobDescription: (state, action) => {
      state.jobDescription = action.payload
    },
    /** @param {TAction<import("./types").JobLocationDict>} action */
    updateJobLocation: (state, action) => {
      state.jobLocationDict = action.payload
    },
    /** @param {TAction<string>} action */
    updateJobApplicationLink: (state, action) => {
      state.jobApplicationLink = action.payload
    },
    resetJobPost: (state) => {
      return { ...state, ...resetState }
    },
    /** @param {TAction<string>} action */
    updateJobExpirationDate: (state, action) => {
      state.jobExpirationDate = action.payload
    },
    /** @param {TAction<number>} action */
    updateJobId: (state, action) => {
      state.jobId = action.payload
    },
    deselectJobPost: (state) => {
      state.loading = false
      state.selectedJobPost = null
      state.postedByConstituent = null
      state.suggestedSearch = null
    },
    editSelectedJobPost: (state) => {
      const selectedJobPost = state.selectedJobPost

      state.jobTitle = selectedJobPost?.positionName || ""
      state.jobCompany = selectedJobPost?.companyName || ""
      state.jobDescription = selectedJobPost?.description || ""
      state.jobApplicationLink = selectedJobPost?.jobApplicationLink || ""
      state.jobExpirationDate = selectedJobPost?.expirationDate || ""
      state.jobLocationDict = {
        city: selectedJobPost?.city || "",
        stateName: selectedJobPost?.stateName || "",
        stateCode: selectedJobPost?.stateCode || "",
        countryCode: selectedJobPost?.countryCode || "",
      }
      state.jobId = selectedJobPost?.id || null
      state.jobIsRemote = selectedJobPost?.isRemote || false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(LOGOUT_RTK, (_state, _action) => initialState)
      .addCase(fetchAllJobPostsThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchAllJobPostsThunk.fulfilled, (state, action) => {
        state.jobPosts = action.payload
        state.loading = false
      })
      .addCase(fetchAllJobPostsThunk.rejected, (state) => {
        state.loading = false
      })
      .addCase(fetchJobPostThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchJobPostThunk.fulfilled, (state, action) => {
        receiveJobPostAndConstituent(state, action.payload)
        state.loading = false
      })
      .addCase(fetchJobPostThunk.rejected, (state) => {
        state.loading = false
      })
      .addCase(submitJobPostThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(submitJobPostThunk.fulfilled, (state, action) => {
        state.jobPosts = action.payload
        state.loading = false
      })
      .addCase(submitJobPostThunk.rejected, (state) => {
        state.loading = false
      })
      .addCase(deleteJobPostThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteJobPostThunk.fulfilled, (state, action) => {
        receiveJobPostAndConstituent(state, action.payload)
        state.jobPosts = action.payload.jobPosts
        state.loading = false
      })
      .addCase(deleteJobPostThunk.rejected, (state) => {
        state.loading = false
      })
      .addCase(editJobPostThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(editJobPostThunk.fulfilled, (state, action) => {
        receiveJobPostAndConstituent(state, action.payload)
        state.jobPosts = action.payload.jobPosts
        state.loading = false
      })
      .addCase(editJobPostThunk.rejected, (state) => {
        state.loading = false
      })
  },
})

export const JobPostsUserActions = {
  ...jobPostsSlice.actions,
  fetchAllJobPosts: fetchAllJobPostsThunk,
  fetchJobPost: fetchJobPostThunk,
  submitJobPost: submitJobPostThunk,
  deleteJobPost: deleteJobPostThunk,
  editJobPost: editJobPostThunk,
}

export default jobPostsSlice.reducer
